import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { Pda, PdaScanEvent } from './components/Pda';
import { Header } from './components/Header';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from './components/ResultTable';
import { useScanQueueConsumer } from './components/useScanQueueConsumer';
import { ScanInput } from './components/ScanInput';
import { isValidBarCode } from 'utils/BarCodeUtil';
import { parseException, showError, parseExceptionMessageKey, getTranslatedMessage } from 'utils/exceptionUtils';
import { playScanSound, playScanFailedAoSound } from 'utils/scanUtils';
import { t } from '../../i18n';
import { formatLocalizedTime } from '../../utils/dateFormatUtils';
import {
    HANDLE_PICKING_SCAN_EVENT,
    HandlePickingScanEventResponse,
    HandlePickingScanEventVariables
} from '../../graphql/mutations/pickingScan';
import {
    GET_PENDING_PICKING_PACKAGE_LIST,
    GetPendingPickingPackageListResponse,
    WarehouseLocationPackageListItem
} from '../../graphql/queries/pickingQueries';
import { LocationOrderScanOverlay, LocationOrderScanData } from './components/LocationOrderScanOverlay';
import { Tabs } from 'antd-mobile';

interface GroupedPackage {
    locationNo: string;
    packages: WarehouseLocationPackageListItem[];
    completedCount: number;
    totalCount: number;
    initialTotalCount?: number; // 初始总数量，用于固定分母
}

const PageBody = () => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [trackingNumberInput, setTrackingNumberInput] = useState('');
    const [nextSequenceNo, setNextSequenceNo] = useState(1);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayData, setOverlayData] = useState<LocationOrderScanData | null>(null);
    const [overlayColor, setOverlayColor] = useState<'green' | 'red' | 'blue' | 'orange'>('green');
    const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');
    
    // 保存初始化时的固定值
    const [initialPendingCount, setInitialPendingCount] = useState<number | null>(null);
    const [initialLocationCount, setInitialLocationCount] = useState<number | null>(null);
    const [initialLocationTotalCounts, setInitialLocationTotalCounts] = useState<Record<string, number>>({});

    const trackingNumberRef = useRef<HTMLInputElement>(null);

    // 获取包裹列表
    const { data: packageListData, refetch: refetchPackageList } = useQuery<GetPendingPickingPackageListResponse>(
        GET_PENDING_PICKING_PACKAGE_LIST,
        {
            fetchPolicy: 'network-only'
        }
    );

    const apolloClient = useApolloClient();
    const [handlePickingScanEvent] = useMutation<HandlePickingScanEventResponse, HandlePickingScanEventVariables>(
        HANDLE_PICKING_SCAN_EVENT
    );

    // refs for state read inside onProcess
    const overlayVisibleRef = useRef(overlayVisible);
    overlayVisibleRef.current = overlayVisible;
    const nextSequenceNoRef = useRef(nextSequenceNo);
    nextSequenceNoRef.current = nextSequenceNo;

    const onProcess = useCallback(async (event: PdaScanEvent) => {
        const code = event.code;

        // 进行下一个扫描时，关闭上一个弹窗
        if (overlayVisibleRef.current) {
            setOverlayVisible(false);
            setOverlayData(null);
        }

        setTrackingNumberInput(code);

        try {
            const { data } = await handlePickingScanEvent({
                variables: {
                    trackingNumber: code
                }
            });
            const result = data?.handlePickingScanEvent;
            if (result?.success) {
                playScanSound();

                // 记录成功扫描 - 序号格式化为4位数字
                const sequenceNo = String(nextSequenceNoRef.current).padStart(4, '0');
                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: code,
                    time: formatLocalizedTime(),
                    value1: sequenceNo,
                    seq: event.seq
                })));
                setNextSequenceNo(prev => prev + 1);

                // 设置展示数据
                if (result.packageInfo) {
                    const instruction = result.packageInfo.pickingActionDetail;
                    // 根据pickingActionDetail判断颜色：重派绿色，重打面单重派蓝色，退回商家仓红色，退回hub销毁橙色
                    const color = instruction === 'ReDeliver' ? 'green' :
                                 instruction === 'RePrintReDeliver' ? 'blue' :
                                 instruction === 'ReturnToMerchant' ? 'red' :
                                 instruction === 'ReturnToHubDestroy' ? 'orange' :
                                 instruction === 'Return' ? 'red' : 'green';

                    setOverlayColor(color);
                    setOverlayData({
                        pickingActionDetail: instruction || result.packageInfo.nextAction
                    });
                    setOverlayVisible(true);
                }

                // 手动更新 Apollo Cache，避免额外的网络请求
                try {
                    const cacheData = apolloClient.readQuery<GetPendingPickingPackageListResponse>({
                        query: GET_PENDING_PICKING_PACKAGE_LIST
                    });

                    if (cacheData?.getPendingPickingPackageList) {
                        const updatedPackages = cacheData.getPendingPickingPackageList.map(pkg =>
                            pkg.trackingNumber === code
                                ? { ...pkg, pickingStatus: 2 as const }
                                : pkg
                        );

                        apolloClient.writeQuery<GetPendingPickingPackageListResponse>({
                            query: GET_PENDING_PICKING_PACKAGE_LIST,
                            data: {
                                getPendingPickingPackageList: updatedPackages
                            }
                        });
                    }
                } catch (error) {
                    refetchPackageList();
                }

                setTrackingNumberInput('');
            } else {
                playScanFailedAoSound();
                const messageKey = result?.messageKey;
                let errorMessage = messageKey ? getTranslatedMessage(messageKey, t('pda.common.scanFailed')) : t('pda.common.scanFailed');
                let failedReason = errorMessage;

                if (messageKey) {
                    const reasonMap: Record<string, string> = {
                        'errors.business.picking.packageNotOnShelf': t('errors.business.picking.packageNotOnShelf'),
                        'errors.business.picking.nextActionIsHold': t('errors.business.picking.nextActionIsHold'),
                        'errors.business.order.notExist': t('errors.business.order.notExist'),
                        'errors.business.package.notBelongToWarehouse': t('errors.business.package.notBelongToWarehouse.message')
                    };
                    failedReason = reasonMap[messageKey] || errorMessage;
                }

                showError({
                    title: t('pda.common.scanFailed'),
                    message: errorMessage
                });

                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: code,
                    failedReason: failedReason,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));

                setTrackingNumberInput('');
            }
        } catch (error: any) {
            playScanFailedAoSound();
            const messageKey = parseExceptionMessageKey(error);
            const { title, message } = parseException(error);
            let failedReason = message;

            if (messageKey) {
                const reasonMap: Record<string, string> = {
                    'errors.business.picking.packageNotOnShelf': t('errors.business.picking.packageNotOnShelf'),
                    'errors.business.picking.nextActionIsHold': t('errors.business.picking.nextActionIsHold'),
                    'errors.business.order.notExist': t('errors.business.order.notExist'),
                    'errors.business.package.notBelongToWarehouse': t('errors.business.package.notBelongToWarehouse.message')
                };
                failedReason = reasonMap[messageKey] || message;
            }

            showError({ title, message });

            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: code,
                failedReason: failedReason,
                time: formatLocalizedTime(),
                seq: event.seq
            })));

            setTrackingNumberInput('');
        }
    }, [handlePickingScanEvent, apolloClient, refetchPackageList]);

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    // 初始化时保存每个库位的初始总数量
    useEffect(() => {
        if (Object.keys(initialLocationTotalCounts).length === 0 && packageListData?.getPendingPickingPackageList) {
            const allPackages = packageListData.getPendingPickingPackageList;
            const locationTotalCounts: Record<string, number> = {};
            
            // 只统计待下架的包裹来计算每个库位的初始总数量
            allPackages
                .filter(pkg => pkg.pickingStatus === 1)
                .forEach(pkg => {
                    if (!locationTotalCounts[pkg.locationNo]) {
                        locationTotalCounts[pkg.locationNo] = 0;
                    }
                    locationTotalCounts[pkg.locationNo]++;
                });
            
            setInitialLocationTotalCounts(locationTotalCounts);
        }
    }, [packageListData, initialLocationTotalCounts]);

    // 处理数据排序和分组
    const groupedPackages = useMemo(() => {
        if (!packageListData?.getPendingPickingPackageList) {
            return { pending: [], completed: [] };
        }

        const allPackages = packageListData.getPendingPickingPackageList;
        
        // 先按buildTime倒序排
        const sortedByBuildTime = [...allPackages].sort((a, b) => {
            const buildTimeA = a.buildTime || 0;
            const buildTimeB = b.buildTime || 0;
            return buildTimeB - buildTimeA;
        });

        // 按locationNo分组
        const groupedByLocation: Record<string, WarehouseLocationPackageListItem[]> = {};
        sortedByBuildTime.forEach(pkg => {
            if (!groupedByLocation[pkg.locationNo]) {
                groupedByLocation[pkg.locationNo] = [];
            }
            groupedByLocation[pkg.locationNo].push(pkg);
        });

        // 每个组内按updatedAt倒序排
        Object.keys(groupedByLocation).forEach(locationNo => {
            groupedByLocation[locationNo].sort((a, b) => {
                const updatedAtA = a.updatedAt || 0;
                const updatedAtB = b.updatedAt || 0;
                return updatedAtB - updatedAtA;
            });
        });

        // 分离待下架和已下架
        const pending: GroupedPackage[] = [];
        const completed: GroupedPackage[] = [];

        Object.keys(groupedByLocation).forEach(locationNo => {
            const packages = groupedByLocation[locationNo];
            const pendingPackages = packages.filter(pkg => pkg.pickingStatus === 1);
            const completedPackages = packages.filter(pkg => pkg.pickingStatus === 2);
            
            // 使用初始总数量（如果已保存）或当前总数量
            const totalCount = initialLocationTotalCounts[locationNo] ?? packages.length;

            if (pendingPackages.length > 0) {
                pending.push({
                    locationNo,
                    packages: pendingPackages,
                    completedCount: completedPackages.length,
                    totalCount: totalCount
                });
            }

            if (completedPackages.length > 0) {
                completed.push({
                    locationNo,
                    packages: completedPackages,
                    completedCount: completedPackages.length,
                    totalCount: totalCount
                });
            }
        });

        return { pending, completed };
    }, [packageListData, initialLocationTotalCounts]);

    // 统一的扫描入口
    const handleScan = (code: string) => {
        if (!isValidBarCode(code)) {
            return;
        }
        // 进行下一个扫描时，关闭上一个弹窗
        if (overlayVisible) {
            setOverlayVisible(false);
            setOverlayData(null);
        }
        enqueueScan(code);
    };

    // 当用户手动修改或删除运单号时的处理
    const handleTrackingNumberInputChange = (value: string) => {
        setTrackingNumberInput(value);
    };

    const handleOverlayClose = () => {
        setOverlayVisible(false);
        setOverlayData(null);
    };

    const currentGroupedPackages = activeTab === 'pending' ? groupedPackages.pending : groupedPackages.completed;
    const pendingCount = groupedPackages.pending.reduce((sum, group) => sum + group.packages.length, 0);
    const completedCount = groupedPackages.completed.reduce((sum, group) => sum + group.packages.length, 0);
    const locationCount = groupedPackages.pending.length;

    // 初始化时保存固定值（总待下架数量和库位数量）
    useEffect(() => {
        if (initialPendingCount === null && packageListData?.getPendingPickingPackageList) {
            const allPackages = packageListData.getPendingPickingPackageList;
            const initialPending = allPackages.filter(pkg => pkg.pickingStatus === 1).length;
            const initialLocations = new Set(allPackages
                .filter(pkg => pkg.pickingStatus === 1)
                .map(pkg => pkg.locationNo)
            ).size;
            
            setInitialPendingCount(initialPending);
            setInitialLocationCount(initialLocations);
        }
    }, [packageListData, initialPendingCount, initialLocationCount]);

    // 使用固定的初始值或当前值（如果还未初始化）
    const displayPendingCount = initialPendingCount !== null ? initialPendingCount : pendingCount;
    const displayLocationCount = initialLocationCount !== null ? initialLocationCount : locationCount;

    // 定义表格列
    const successColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.no'), key: 'value1' }
    ];

    const failedColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    // 格式化成功记录的序号为4位数字
    const formattedSuccessRecords = successRecords.map(record => ({
        ...record,
        value1: record.value1 ? String(record.value1).padStart(4, '0') : record.value1
    }));

    return (
        <div className="flex flex-col gap-2 flex-1 overflow-hidden">
            {/* 扫描输入框 */}
            <div className="px-2">
                <div className="text-sm font-medium mb-1">{t('pda.pages.picking.trackingNumber')}</div>
                <ScanInput
                    ref={trackingNumberRef}
                    placeholder={t('pda.pages.picking.scanTrackingNumber')}
                    value={trackingNumberInput}
                    onScan={handleScan}
                    onChange={handleTrackingNumberInputChange}
                />
            </div>

            {/* Tab切换 */}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as 'pending' | 'completed')}
                className="border-b font-bold"
            >
                <Tabs.Tab title={`${t('pda.pages.picking.pending')}(${pendingCount})`} key="pending" />
                <Tabs.Tab title={`${t('pda.pages.picking.completed')}(${completedCount})`} key="completed" />
            </Tabs>

            {/* 摘要信息 */}
            {
                <div className="px-2 flex justify-between text-sm text-gray-500">
                    <span>{t('pda.pages.picking.locationsToPick')}: {displayLocationCount}</span>
                    <span>{t('pda.pages.picking.completedPending')}: {completedCount}/{displayPendingCount}</span>
                </div>
            }

            {/* 包裹列表 - 只在待下架tab显示 */}
            {activeTab === 'pending' && (
                <div className="flex-1 overflow-auto px-2">
                    {currentGroupedPackages.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    ) : (
                        currentGroupedPackages.map((group) => (
                            <div key={group.locationNo} className="mb-4">
                                {/* 库位头部 */}
                                <div className="flex justify-between items-center p-2 bg-gray-100 rounded-t">
                                    <span className="text-lg font-bold break-all">{group.locationNo}</span>
                                    <span className="text-sm text-gray-600">
                                        {t('pda.pages.picking.count')}: {group.completedCount}/{group.totalCount}
                                    </span>
                                </div>
                                {/* 包裹列表 */}
                                <div className="border border-t-0 rounded-b">
                                    {group.packages.map((pkg) => (
                                        <div key={pkg.trackingNumber} className="p-2 border-b last:border-b-0 flex justify-between items-center">
                                            <span className="flex-1 font-bold break-all">{pkg.trackingNumber}</span>
                                            <div className="flex gap-2 text-f">
                                                {(pkg.pickingActionDetail || pkg.nextAction) && (() => {
                                                    const instruction = pkg.pickingActionDetail || pkg.nextAction;
                                                    return (
                                                    <span className={`text-sm font-bold ${
                                                        instruction === 'ReDeliver' ? 'text-green-600' :
                                                        instruction === 'RePrintReDeliver' ? 'text-blue-600' :
                                                        instruction === 'ReturnToMerchant' ? 'text-red-600' :
                                                        instruction === 'ReturnToHubDestroy' ? 'text-orange-600' :
                                                        instruction === 'Return' ? 'text-red-600' : ''
                                                    }`}>
                                                        {instruction === 'ReDeliver' ? t('pda.pages.picking.redispatch') :
                                                         instruction === 'RePrintReDeliver' ? t('pda.pages.picking.rePrintRedispatch') :
                                                         instruction === 'ReturnToMerchant' ? t('pda.pages.picking.returnToMerchant') :
                                                         instruction === 'ReturnToHubDestroy' ? t('pda.pages.picking.returnToHubDestroy') :
                                                         instruction === 'Return' ? t('pda.pages.picking.return') :
                                                         instruction}
                                                    </span>
                                                    );
                                                })()}
                                                {pkg.locationOrderNo && (
                                                    <span className="text-sm font-bold">
                                                        {String(pkg.locationOrderNo).padStart(3, '0')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* 扫描结果表格 - 只在已下架tab显示 */}
            {activeTab === 'completed' && (
                <div className="flex-1 overflow-auto px-2">
                    <ResultTable
                        successRecords={formattedSuccessRecords}
                        failedRecords={failedRecords}
                        successColumns={successColumns}
                        failedColumns={failedColumns}
                    />
                </div>
            )}

            {/* 扫描成功弹窗 */}
            {overlayData && (
                <LocationOrderScanOverlay
                    visible={overlayVisible}
                    data={overlayData}
                    onClose={handleOverlayClose}
                    color={overlayColor}
                />
            )}
        </div>
    );
};

export default function PickingScanPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.picking.picking')} />
            <Pda />
            <PageBody />
        </div>
    );
}
